import React, { useState } from 'react';

const LOCATIONS = [
  { name: 'Israeli state', code: 'IL' },
  { name: 'United States', code: 'US' },
  { name: 'United Kingdom', code: 'GB' },
  { name: 'Canada', code: 'CA' },
  { name: 'Germany', code: 'DE' },
  { name: 'Australia', code: 'AU' },
  { name: 'France', code: 'FR' },
  { name: 'Netherlands', code: 'NL' },
  { name: 'India', code: 'IN' },
];

const SCOPES = ['Local', 'National', 'International', 'Intergovernmental'];

function slugify(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export default function OrgForm({ org = null, orgs = [], onSave, onClose }) {
  const isEdit = !!org;
  const [form, setForm] = useState({
    name: org?.name || '',
    fullName: org?.fullName || '',
    acronym: org?.acronym || '',
    country: org?.country || '',
    countryCode: org?.countryCode || '',
    founded: org?.founded || '',
    description: org?.description || '',
    website: org?.website || '',
    archivedWebsite: org?.archivedWebsite || '',
    parentOrg: org?.parentOrg || '',
    scope: org?.scope || '',
    tags: org?.tags || [],
    sources: org?.sources || [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleCountryChange = (countryName) => {
    const found = LOCATIONS.find(c => c.name === countryName);
    set('country', countryName);
    if (found) set('countryCode', found.code);
  };

  const addSource = () => set('sources', [...form.sources, { name: '', url: '' }]);
  const updateSource = (idx, field, value) => {
    const updated = [...form.sources];
    updated[idx] = { ...updated[idx], [field]: value };
    set('sources', updated);
  };
  const removeSource = (idx) => set('sources', form.sources.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!form.description.trim()) { setError('Description is required'); return; }

    setSaving(true);
    setError('');
    try {
      const data = {
        ...form,
        slug: org?.slug || slugify(form.name),
        id: org?.id || slugify(form.name),
        sources: form.sources.filter(s => s.name.trim() || s.url.trim()),
      };
      await onSave(data);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div className="admin-modal admin-modal-wide" onClick={e => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3>{isEdit ? 'Edit Organisation' : 'New Organisation'}</h3>
          <button className="admin-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="admin-modal-body">
            {error && <div className="admin-error">{error}</div>}

            <label className="admin-label">Name *</label>
            <input className="admin-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Organisation name" />

            <label className="admin-label">Full name</label>
            <input className="admin-input" value={form.fullName} onChange={e => set('fullName', e.target.value)} placeholder="Full legal name (optional)" />

            <label className="admin-label">Acronym</label>
            <input className="admin-input" value={form.acronym} onChange={e => set('acronym', e.target.value)} placeholder="e.g. AIPAC, ADL (optional)" />

            <div className="admin-row">
              <div className="admin-field">
                <label className="admin-label">Location</label>
                <select className="admin-input" value={form.country} onChange={e => handleCountryChange(e.target.value)}>
                  <option value="">Select location...</option>
                  {LOCATIONS.map(c => <option key={c.code} value={c.name}>{c.name}</option>)}
                  <option value="_other">Other</option>
                </select>
                {form.country === '_other' && (
                  <input className="admin-input" placeholder="Location name" onChange={e => set('country', e.target.value)} style={{marginTop: '0.5rem'}} />
                )}
              </div>
              <div className="admin-field">
                <label className="admin-label">Scope</label>
                <select className="admin-input" value={form.scope} onChange={e => set('scope', e.target.value)}>
                  <option value="">Select scope...</option>
                  {SCOPES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div className="admin-row">
              <div className="admin-field">
                <label className="admin-label">Founded</label>
                <input className="admin-input" value={form.founded} onChange={e => set('founded', e.target.value)} placeholder="e.g. 2001" />
              </div>
            </div>

            <label className="admin-label">Description *</label>
            <textarea className="admin-input" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Description of the organisation..." rows={4} />

            <label className="admin-label">Website</label>
            <input className="admin-input" value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://..." />

            <label className="admin-label">Archived website</label>
            <input className="admin-input" value={form.archivedWebsite} onChange={e => set('archivedWebsite', e.target.value)} placeholder="https://web.archive.org/..." />

            <label className="admin-label">Parent organisation</label>
            <select className="admin-input" value={form.parentOrg} onChange={e => set('parentOrg', e.target.value)}>
              <option value="">None</option>
              {orgs.filter(o => o.id !== org?.id).map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>

            <label className="admin-label">Sources</label>
            {form.sources.map((s, idx) => (
              <div key={idx} className="admin-source-row">
                <input className="admin-input" value={s.name} onChange={e => updateSource(idx, 'name', e.target.value)} placeholder="Source name" />
                <input className="admin-input" value={s.url} onChange={e => updateSource(idx, 'url', e.target.value)} placeholder="https://..." />
                <button type="button" className="admin-btn-remove" onClick={() => removeSource(idx)}>×</button>
              </div>
            ))}
            <button type="button" className="admin-btn-add" onClick={addSource}>+ Add source</button>
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="admin-btn-primary" disabled={saving}>
              {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}